import { useCallback, useEffect, useRef, useState } from 'react';
import { useI18n, type CopyKey } from './i18n';
import {
  isError,
  deleteSource,
  listSources,
  retrySource,
  presignUpload,
  readExtracted,
  type ExtractedContent,
  type Source,
} from './api';

/**
 * Adding material, and seeing what was read out of it.
 *
 * The second half is the point. A photograph of a page is only worth uploading if
 * you can tell whether it was read correctly — an extraction nobody checks is a
 * confidently wrong curriculum waiting to happen.
 */

/**
 * What the file picker will offer, matching what the API will sign.
 *
 * **HEIC is deliberately absent, and its absence is the feature.** Textract
 * refuses HEIC outright — measured, on a real one — and an iPhone writes HEIC by
 * default. When the picker asks only for JPEG, iOS converts on the way out, so
 * leaving HEIC off this list is what makes photographing a page from an iPhone
 * work at all. Listing it made the upload succeed and the reading fail.
 */
const ACCEPT = 'image/jpeg,image/png,image/tiff,application/pdf';

/**
 * What the browser says a file is, when it says anything.
 *
 * Some phone browsers hand over an empty `type` for a file picked out of the
 * photo library, and an empty content type is refused by the presign as
 * unsupported — so a perfectly good photograph is rejected for a reason that has
 * nothing to do with it. The extension is the fallback, and it is the same thing
 * the server would key off anyway.
 */
const BY_EXTENSION: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  tif: 'image/tiff',
  tiff: 'image/tiff',
  heic: 'image/heic',
  heif: 'image/heif',
  webp: 'image/webp',
  pdf: 'application/pdf',
};

function typeOf(file: File): string {
  if (file.type) return file.type;
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return BY_EXTENSION[extension] ?? '';
}

/** Megabytes, for a sentence rather than for arithmetic. */
function megabytes(bytes: number): string {
  return String(Math.floor(bytes / 1024 / 1024));
}

/** Polling stops here. Extraction is seconds for an image, longer for a PDF. */
const POLL_ATTEMPTS = 40;
const POLL_INTERVAL_MS = 3000;

/**
 * The id the gap notice sends the learner to.
 *
 * Exported rather than spelled out in both files. The notice on the objective
 * screen finds this panel by id and scrolls to it, and a literal written twice is
 * one rename away from a button that silently does nothing — which is the worst
 * shape of failure for a control whose entire job is to lead somewhere.
 */
export const MATERIAL_PANEL_ID = 'your-material';

export default function Sources({
  objectiveId,
  /** True when the objective has gaps, which changes what this panel is for. */
  wanted = false,
  /**
   * True when the objective already has a plan, so an upload folds itself in.
   *
   * Passed rather than worked out here: this panel is given an id and nothing else, and
   * having it fetch the objective to find out whether a sentence applies would make the
   * material panel depend on the objective screen's own read. What it changes is small
   * and it is the thing Leo asked for — somebody who has just added a page should be
   * told the rest happens, not left looking for the next button.
   */
  automatic = false,
  /**
   * Called when an extraction finishes, so the objective screen can catch up.
   *
   * **Without it the automatic chain is invisible to somebody watching it happen.**
   * The objective screen only polls while it believes something is in flight, and
   * before F-92 nothing was: the fold started when a button was pressed, so the press
   * was the signal. Now an extraction starts it, and the only thing that knows an
   * extraction finished is this panel — so a learner who uploads a page and stays on
   * the screen would sit in front of a panel offering to fold material that is already
   * being folded, and would not see the outcome until they reloaded.
   *
   * Deliberately not called from the mount read. It is a notification that something
   * changed, not a mirror of every list read, and a parent that reloads on every read
   * is one dependency away from two components reloading each other.
   */
  onExtraction,
}: {
  objectiveId: string;
  wanted?: boolean;
  automatic?: boolean;
  onExtraction?: () => void;
}) {
  const { t } = useI18n();
  const [sources, setSources] = useState<Source[] | undefined>();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>();
  const [open, setOpen] = useState<Record<string, ExtractedContent | 'loading' | undefined>>({});
  /*
   * Per-source, and separate from `problem`.
   *
   * `problem` belongs to the upload control at the top; a retry that failed on the
   * fourth row has nothing to do with it. One error state read in two places renders
   * the same complaint against unrelated controls — reported once already as "the
   * error came up in multiple places" — and keying by source id is also what stops
   * two rows acting on each other's spinner.
   */
  const [acting, setActing] = useState<Record<string, 'retry' | 'remove' | undefined>>({});
  const [actionProblem, setActionProblem] = useState<Record<string, string | undefined>>({});
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    const result = await listSources(objectiveId);
    if (!isError(result)) setSources(result.sources);
    else setSources([]);
    return isError(result) ? [] : result.sources;
  }, [objectiveId]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Waits for extraction to finish, then stops.
   *
   * Polling rather than a push, because the alternative is a websocket for one
   * event a learner is already watching for. It gives up rather than spinning
   * forever: a source stuck at `pending` past the timeout is a real failure, and
   * saying so beats a spinner that never resolves.
   */
  async function waitForExtractions(sourceIds: readonly string[]) {
    if (sourceIds.length === 0) return;
    const waiting = new Set(sourceIds);
    for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      const current = await load();
      const before = waiting.size;
      for (const source of current) {
        if (source.status !== 'pending') waiting.delete(source.sourceId);
      }
      // Told once per tick that something settled, rather than once per source: what
      // the objective screen does with it is one read of one row.
      if (waiting.size < before) onExtraction?.();
      if (waiting.size === 0) return;
    }
  }

  /** Each presign refusal in its own words, because each has its own remedy. */
  function explainPresign(error: string, maxBytes?: number): string {
    /*
     * "Something went wrong with that file" over a HEIC photograph sends a learner to
     * look for a bug that is not there — the fix is a camera setting, and it takes one
     * line to say so.
     */
    if (error === 'camera_format') return t('src.error.camera');
    if (error === 'too_large') return `${t('src.error.tooLarge')} ${megabytes(maxBytes ?? 0)} MB.`;
    if (error === 'unsupported_type') return t('src.error.type');
    return t('src.error.generic');
  }

  /**
   * Several pages at once, and **every row is created before a single byte is sent**.
   *
   * That ordering is not a tidy-up, it is what makes a batch of photographs cost one
   * fold instead of eight. `pendingSiblings` in `packages/ingest/src/extract.ts` folds
   * the new material in only when no other source on the objective is still `pending`,
   * so the eight extractions have to be able to see each other waiting — and they can
   * only do that if all eight rows exist by the time the first one finishes. Presigning
   * and uploading one file at a time, which is what this did, produces eight lone
   * uploads: each sees no sibling pending, each folds, and a fold is $0.46 plus the
   * item generation behind it.
   *
   * The picker takes several files for the same reason. Somebody photographing a
   * chapter is doing one thing, not eight, and a control that accepts one file at a
   * time makes the human the thing that serialises them.
   */
  async function upload(files: readonly File[]) {
    setBusy(true);
    setProblem(undefined);

    /*
     * Kept locally rather than read back off `problem`: state does not update inside
     * this loop, and the first refusal is the useful one — a learner who picked one
     * HEIC among seven JPEGs needs to hear about the HEIC, not about whatever the last
     * file did.
     */
    let firstProblem: string | undefined;

    const signed: Array<{ file: File; sourceId: string; url: string }> = [];
    for (const file of files) {
      const result = await presignUpload(objectiveId, typeOf(file), file.name, file.size);
      if (isError(result)) {
        // Refused, and the rest still go. One unacceptable file in a batch is not a
        // reason to make somebody pick the other seven again.
        firstProblem ??= explainPresign(result.error, result.maxBytes);
        continue;
      }
      signed.push({ file, sourceId: result.sourceId, url: result.url });
    }

    // The rows appear as "Reading" while the bytes are still going up, which is what
    // they are: presigned, recorded, and waited on by everything downstream.
    if (signed.length > 0) await load();

    for (const { file, sourceId, url } of signed) {
      try {
        /*
         * Straight to S3, with the same content type that was signed.
         *
         * `file.type` rather than `typeOf(file)` stored `binary/octet-stream` on
         * every upload from a browser that reports no type — the object is still
         * extractable, because routing is by extension, but F-46 reads this field
         * to send the crop back to a phone and a wrong one there is a broken image.
         */
        const response = await fetch(url, {
          method: 'PUT',
          headers: { 'content-type': typeOf(file) },
          body: file,
        });
        if (!response.ok) throw new Error(`S3 returned ${response.status}`);
      } catch {
        firstProblem ??= t('src.error.upload');
        /*
         * The row goes with the failed upload, and it has to.
         *
         * A presigned row nothing ever uploads says `pending` for ever: it shows on
         * this list as "Reading" with no extraction coming, and — since F-92 — it
         * holds up the automatic fold of every *other* page in the batch, because a
         * pending sibling is how "more is on its way" is expressed. Best effort: if
         * the delete also fails the row ages out of the fold rule on its own after
         * fifteen minutes, which is the fallback that exists for exactly this.
         */
        await deleteSource(objectiveId, sourceId).catch(() => undefined);
      }
    }

    await load();
    await waitForExtractions(signed.map((entry) => entry.sourceId));
    setBusy(false);
    if (firstProblem) setProblem(firstProblem);
    if (fileRef.current) fileRef.current.value = '';
  }

  async function toggle(sourceId: string) {
    if (open[sourceId]) {
      setOpen((prior) => ({ ...prior, [sourceId]: undefined }));
      return;
    }
    setOpen((prior) => ({ ...prior, [sourceId]: 'loading' }));
    const result = await readExtracted(objectiveId, sourceId);
    setOpen((prior) => ({
      ...prior,
      [sourceId]: isError(result) ? undefined : result.content,
    }));
  }

  /*
   * Both actions reload the list rather than patching it in place.
   *
   * A retry moves the row back to "Reading" and extraction finishes minutes later, so
   * the list is the only thing that knows the real state — a local edit would show a
   * status this screen invented. The reload is also what makes a removed row vanish.
   */
  const act = useCallback(
    async (source: Source, what: 'retry' | 'remove') => {
      if (what === 'remove' && !confirm(t('src.remove.confirm'))) return;

      setActing((was) => ({ ...was, [source.sourceId]: what }));
      setActionProblem((was) => ({ ...was, [source.sourceId]: undefined }));

      const result =
        what === 'retry'
          ? await retrySource(objectiveId, source.sourceId)
          : await deleteSource(objectiveId, source.sourceId);

      setActing((was) => ({ ...was, [source.sourceId]: undefined }));
      if (isError(result)) {
        setActionProblem((was) => ({ ...was, [source.sourceId]: t('src.action.failed') }));
        return;
      }
      await load();
    },
    [objectiveId, load, t],
  );

  return (
    /*
     * `tabIndex` so the gap notice can move focus here and not only the viewport.
     * A button that scrolls the page and leaves focus behind strands a keyboard or
     * screen-reader user on the notice they just acted on, with the control they
     * were sent to nowhere near them.
     */
    <section className="panel" id={MATERIAL_PANEL_ID} tabIndex={-1}>
      <h2>{t('src.title')}</h2>
      {/* "Optional" is the wrong first word for somebody who arrived from the gap
          notice: it invites them to reconsider the one thing on this screen that
          only they can do. */}
      <p className="muted">{wanted ? t('src.hint.wanted') : t('src.hint')}</p>
      {/* Said here as well as on the fold panel, because this is where the learner is
          looking at the moment they finish adding a page. */}
      {automatic && <p className="muted small">{t('src.automatic')}</p>}

      <div className="actions">
        <label className="button primary file-button">
          {busy ? t('src.working') : t('src.choose')}
          {/* `multiple` is load-bearing rather than a convenience — see `upload`. */}
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPT}
            multiple
            disabled={busy}
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              if (files.length > 0) void upload(files);
            }}
          />
        </label>
      </div>

      {problem && (
        <p className="problem" role="alert">
          {problem}
        </p>
      )}

      {sources?.length === 0 && <p className="muted">{t('src.empty')}</p>}

      <ul className="sources">
        {sources?.map((source) => (
          <li key={source.sourceId}>
            <p className="meta">
              <span className={`pill pill-${source.status}`}>
                {t(`src.status.${source.status}` as CopyKey)}
              </span>{' '}
              {source.title ?? source.sourceId}
            </p>

            {source.status === 'failed' && (
              /*
               * Named, not generalised: somebody who photographed eight pages
               * needs to know which one was unreadable, and why, in their own
               * language. The reason is a code with a sentence written for it;
               * `error` is English and quotes AWS, so it is the last resort
               * rather than the first — it once showed a learner an IAM policy
               * denial with a role ARN in it.
               */
              <p className="muted">
                {source.reason
                  ? t(`src.failed.${source.reason}` as CopyKey)
                  : (source.error ?? t('src.error.generic'))}
              </p>
            )}

            {/*
              * Something to do about it, which is the whole point of this block.
              *
              * A row that says "could not read" and offers nothing is litter in the
              * learner's own list of material, and it accumulates. Retry is offered
              * only on an upload, because a link or a researched page has no file of
              * ours to read again; remove is offered on anything, since the reason to
              * take a page away is often that it was the wrong page rather than that
              * it failed.
              */}
            {(source.status === 'failed' || source.status === 'extracted') && (
              <p className="actions-inline">
                {source.status === 'failed' && source.kind === 'upload' && (
                  <button
                    type="button"
                    className="linky"
                    onClick={() => void act(source, 'retry')}
                    disabled={acting[source.sourceId] !== undefined}
                  >
                    {acting[source.sourceId] === 'retry' ? t('src.retrying') : t('src.retry')}
                  </button>
                )}
                <button
                  type="button"
                  className="linky"
                  onClick={() => void act(source, 'remove')}
                  disabled={acting[source.sourceId] !== undefined}
                >
                  {acting[source.sourceId] === 'remove' ? t('src.removing') : t('src.remove')}
                </button>
              </p>
            )}

            {actionProblem[source.sourceId] && (
              <p className="problem" role="alert">
                {actionProblem[source.sourceId]}
              </p>
            )}

            {source.status === 'extracted' && (
              <>
                <button type="button" className="linky" onClick={() => void toggle(source.sourceId)}>
                  {open[source.sourceId] ? t('src.hide') : t('src.show')}
                </button>
                {open[source.sourceId] === 'loading' && <p className="muted">{t('src.loading')}</p>}
                {open[source.sourceId] && open[source.sourceId] !== 'loading' && (
                  <>
                    <p className="muted small">
                      {(open[source.sourceId] as ExtractedContent).method} ·{' '}
                      {(open[source.sourceId] as ExtractedContent).body.length}{' '}
                      {t('src.characters')}
                    </p>
                    <pre className="extracted">
                      {(open[source.sourceId] as ExtractedContent).body}
                      {(open[source.sourceId] as ExtractedContent).truncated
                        ? `\n\n… ${t('src.truncated')}`
                        : ''}
                    </pre>
                  </>
                )}
              </>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
